import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, animeByCategory } from '@/mocks/anime';

const defaultCategory = 'japanese';

const categoryImagesMap: Record<string, string[]> = {
  japanese: [
    'https://readdy.ai/api/search-image?query=Japanese%20anime%20art%20style%20warrior%20character%20with%20katana%20in%20traditional%20setting%2C%20warm%20orange%20and%20coral%20palette%2C%20dynamic%20pose%2C%20clean%20flat%20illustration%20style%2C%20simple%20gradient%20background&width=400&height=560&seq=feat-jp-01&orientation=portrait',
    'https://readdy.ai/api/search-image?query=Japanese%20anime%20style%20sorcerer%20character%20with%20magical%20energy%2C%20warm%20coral%20and%20amber%20palette%2C%20clean%20flat%20illustration%20style%2C%20simple%20gradient%20background&width=400&height=560&seq=feat-jp-02&orientation=portrait',
    'https://readdy.ai/api/search-image?query=Japanese%20anime%20style%20elf%20mage%20in%20fantasy%20meadow%2C%20warm%20golden%20sunlight%2C%20clean%20flat%20illustration%20style%2C%20simple%20gradient%20background&width=400&height=560&seq=feat-jp-03&orientation=portrait',
    'https://readdy.ai/api/search-image?query=Japanese%20anime%20style%20idol%20singer%20on%20stage%2C%20warm%20stage%20lighting%2C%20clean%20flat%20illustration%20style%2C%20simple%20gradient%20background&width=400&height=560&seq=feat-jp-04&orientation=portrait',
    'https://readdy.ai/api/search-image?query=Chinese%20donghua%20style%20young%20cultivator%20with%20spiritual%20energy%2C%20warm%20coral%20palette%2C%20clean%20flat%20illustration%20style%2C%20simple%20gradient%20background&width=400&height=560&seq=feat-jp-05&orientation=portrait',
    'https://readdy.ai/api/search-image?query=Chinese%20donghua%20style%20martial%20artist%20in%20ancient%20fantasy%20world%2C%20warm%20amber%20palette%2C%20clean%20flat%20illustration%20style%2C%20simple%20gradient%20background&width=400&height=560&seq=feat-jp-06&orientation=portrait',
  ],
};

const categoryBannerImages: Record<string, string> = {
  japanese: 'https://readdy.ai/api/search-image?query=Japanese%20anime%20style%20illustration%20panoramic%20view%20of%20traditional%20Japanese%20street%20with%20cherry%20blossoms%2C%20warm%20coral%20and%20orange%20tones%2C%20cinematic%20lighting%2C%20clean%20flat%20illustration%20style&width=1800&height=400&seq=cat-hero-jp&orientation=landscape',
  chinese: 'https://readdy.ai/api/search-image?query=Chinese%20donghua%20style%20panoramic%20view%20of%20ancient%20Chinese%20mountain%20landscape%20with%20temples%2C%20warm%20golden%20and%20coral%20tones%2C%20cinematic%20lighting%2C%20clean%20flat%20illustration%20style&width=1800&height=400&seq=cat-hero-cn&orientation=landscape',
  hot: 'https://readdy.ai/api/search-image?query=Anime%20style%20panoramic%20illustration%20of%20fiery%20dynamic%20battle%20scene%2C%20warm%20orange%20and%20amber%20dramatic%20lighting%2C%20clean%20flat%20illustration%20style%2C%20epic%20cinematic&width=1800&height=400&seq=cat-hero-hot&orientation=landscape',
  classic: 'https://readdy.ai/api/search-image?query=Anime%20style%20panoramic%20illustration%20of%20nostalgic%20vintage%20Japanese%20town%20at%20sunset%2C%20warm%20coral%20and%20amber%20tones%2C%20film%20grain%2C%20clean%20flat%20illustration%20style&width=1800&height=400&seq=cat-hero-cl&orientation=landscape',
  movie: 'https://readdy.ai/api/search-image?query=Cinematic%20anime%20style%20panoramic%20illustration%20of%20grand%20theater%20interior%20with%20dramatic%20lighting%2C%20warm%20orange%20and%20coral%20tones%2C%20clean%20flat%20illustration%20style&width=1800&height=400&seq=cat-hero-mv&orientation=landscape',
  ova: 'https://readdy.ai/api/search-image?query=Anime%20style%20panoramic%20illustration%20of%20cozy%20home%20entertainment%20room%20with%20warm%20ambient%20lighting%2C%20coral%20and%20amber%20tones%2C%20clean%20flat%20illustration%20style&width=1800&height=400&seq=cat-hero-ova&orientation=landscape',
};

const articleImagesMap: Record<string, string[]> = {
  japanese: [
    'https://readdy.ai/api/search-image?query=Japanese%20anime%20art%20style%20warrior%20character%20portrait%20with%20warm%20coral%20lighting%2C%20dynamic%20expression%2C%20clean%20flat%20illustration%20style%2C%20simple%20gradient%20background&width=400&height=560&seq=art-jp-01&orientation=portrait',
    'https://readdy.ai/api/search-image?query=Japanese%20anime%20style%20schoolgirl%20in%20uniform%20under%20cherry%20blossom%20tree%2C%20warm%20pink%20and%20coral%20palette%2C%20clean%20flat%20illustration%20style%2C%20simple%20gradient%20background&width=400&height=560&seq=art-jp-02&orientation=portrait',
    'https://readdy.ai/api/search-image?query=Japanese%20anime%20style%20mecha%20robot%20pilot%20in%20cockpit%2C%20warm%20orange%20interface%20lighting%2C%20clean%20flat%20illustration%20style%2C%20simple%20gradient%20background&width=400&height=560&seq=art-jp-03&orientation=portrait',
  ],
};

function getAnimeImages(key: string): string[] {
  if (categoryImagesMap[key]) return categoryImagesMap[key];
  const fallback = categoryImagesMap.japanese || [];
  return fallback;
}

function getArticleImages(key: string): string[] {
  if (articleImagesMap[key]) return articleImagesMap[key];
  return articleImagesMap.japanese || [];
}

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || defaultCategory;

  const activeCategory = useMemo(
    () => categories.find((c) => c.slug === activeTab) || categories[0],
    [activeTab],
  );

  const animeList = useMemo(
    () => animeByCategory[activeTab] || animeByCategory[defaultCategory] || [],
    [activeTab],
  );

  const animeImages = useMemo(() => getAnimeImages(activeTab), [activeTab]);
  const bannerImage = categoryBannerImages[activeTab] || categoryBannerImages[defaultCategory];

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${activeCategory.name} - 奶片动漫`,
    description: `奶片动漫${activeCategory.name}分类，共收录${activeCategory.count}部作品`,
    url: `https://naipiananime.com/category?tab=${activeTab}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '奶片动漫',
      url: 'https://naipiananime.com',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://naipiananime.com' },
      { '@type': 'ListItem', position: 2, name: '分类浏览', item: 'https://naipiananime.com/category' },
      { '@type': 'ListItem', position: 3, name: activeCategory.name },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${activeCategory.name} - 分类浏览`}
        description={`奶片动漫${activeCategory.name}分类，共收录${activeCategory.count}部动漫作品，高清流畅播放，享受追番乐趣。`}
        keywords={`奶片动漫,${activeCategory.name},动漫分类,在线观看`}
        canonicalUrl={`https://naipiananime.com/category?tab=${activeTab}`}
        schema={{ ...schema, ...breadcrumbSchema } as unknown as Record<string, unknown>}
      />

      <main>
        <section className="relative w-full h-[260px] md:h-[360px] overflow-hidden">
          <img
            src={bannerImage}
            alt={activeCategory.name}
            title={`奶片动漫 - ${activeCategory.name}`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-2">
                {activeCategory.name}
              </h1>
              <p className="text-sm md:text-base text-white/70">
                共 {activeCategory.count} 部作品
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '分类浏览', href: '/category' },
              { label: activeCategory.name },
            ]}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 pb-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSearchParams({ tab: cat.slug })}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  activeTab === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        <section className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
          {animeList.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {animeList.map((anime, index) => (
                <Link
                  key={anime.id}
                  to={`/article/${anime.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative w-full aspect-[5/7] rounded-lg overflow-hidden bg-background-200 mb-3">
                    <img
                      src={animeImages[index % animeImages.length]}
                      alt={anime.title}
                      title={anime.title + ' - 奶片动漫高清播放'}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 bg-accent-500 text-white text-xs rounded-md font-medium whitespace-nowrap">
                        {anime.rating}
                      </span>
                    </div>
                    {anime.status === '连载中' && (
                      <div className="absolute top-2 right-2">
                        <span className="px-2 py-0.5 bg-primary-500 text-white text-xs rounded-md font-medium whitespace-nowrap">
                          连载
                        </span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                    {anime.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1">
                    {anime.category} · {anime.episodes}集
                  </p>
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>
    </>
  );
}